import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import pl from 'date-fns/locale/pl';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';

// BackButton component (same as in other components)
const BackButton = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => navigate('/mecz/turniej-rankingowy')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('turniej-rankingowy', language);
        } else if (seg === 'dodaj') {
          displayText = getTranslation('dodaj-turniej', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const AddTournament = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    tournamentdate: '',
    tournamentname: '',
    numberofplayers: 1,
    tournamentType: 'single', // New field to track the specific type
    city: '',
    player2: '',
    player3: '',
    player4: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);

  // Fetch open tournaments
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        }
        
        const response = await fetch('/api/tournaments/open', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Błąd ${response.status}: ${await response.text()}`);
        }
        
        const data = await response.json();
        setTournaments(data);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Błąd połączenia: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'tournamentType') {
      // Map tournament type to numberofplayers
      const typeMapping = {
        'single': 1,
        'doubles': 2,
        'triples': 3,
        'triples_reserve': 4
      };
      
      setFormData(prev => ({
        ...prev,
        tournamentType: value,
        numberofplayers: typeMapping[value],
        // Clear player fields when changing type
        player2: '',
        player3: '',
        player4: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      }
      
      const response = await fetch('/api/tournaments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Błąd ${response.status}: ${await response.text()}`);
      }
      
      // Reset form and go back to list
      setFormData({
        tournamentdate: '',
        tournamentname: '',
        numberofplayers: 1,
        tournamentType: 'single',
        city: '',
        player2: '',
        player3: '',
        player4: ''
      });
      setShowAddForm(false);
      
      // Refresh tournaments list
      const tournamentsResponse = await fetch('/api/tournaments/open', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (tournamentsResponse.ok) {
        const tournamentsData = await tournamentsResponse.json();
        setTournaments(tournamentsData);
      }
      
    } catch (err) {
      console.error('Submit error:', err);
      setError(`Błąd podczas dodawania turnieju: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getTournamentType = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: return getTranslation('single', language);
      case 2: return getTranslation('dublety', language);
      case 3: return getTranslation('triplety', language);
      case 4: return getTranslation('triplety-z-rezerwowym', language);
      default: return getTranslation('nieznany', language);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const handleDeleteClick = (tournament) => {
    setTournamentToDelete(tournament);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tournamentToDelete) return;
    
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      }
      
      const response = await fetch(`/api/tournaments/${tournamentToDelete.tournamentid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Błąd ${response.status}: ${await response.text()}`);
      }
      
      // Refresh tournaments list
      const tournamentsResponse = await fetch('/api/tournaments/open', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (tournamentsResponse.ok) {
        const tournamentsData = await tournamentsResponse.json();
        setTournaments(tournamentsData);
      }
      
      setShowDeleteModal(false);
      setTournamentToDelete(null);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Błąd podczas usuwania turnieju: ${err.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTournamentToDelete(null);
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              Błąd ładowania turniejów
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>

      {!showAddForm ? (
        // Tournament List View
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem',
              textAlign: 'center'
            }}>
              {getTranslation('turnieje-w-trakcie-rozgrywek', language)}
            </h2>
            
            {tournaments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ 
                  fontFamily: 'Nunito, Arial, sans-serif', 
                  fontSize: '1.2rem', 
                  color: '#666' 
                }}>
                  {getTranslation('brak-turniejow-w-trakcie', language)}
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontFamily: 'Nunito, Arial, sans-serif'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('szczegoly', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('usun', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('data', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('miasto', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('nazwa-turnieju', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('typ', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('ilosc-graczy', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz2', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('gracz3', language)}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('rezerwowy', language)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tournaments.map((tournament) => (
                      <tr key={tournament.tournamentid} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => navigate(`/mecz/turniej-rankingowy/szczegoly/${tournament.tournamentid}?from=dodaj`)}
                            style={{
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontFamily: 'Nunito, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#2563eb'}
                            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                          >
                            {getTranslation('szczegoly', language)}
                          </button>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteClick(tournament)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontFamily: 'Nunito, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#dc2626'}
                            onMouseOut={(e) => e.target.style.background = '#ef4444'}
                          >
                            {getTranslation('usun', language)}
                          </button>
                        </td>
                        <td style={{ padding: '12px' }}>{formatDate(tournament.tournamentdate)}</td>
                        <td style={{ padding: '12px' }}>{tournament.city || '-'}</td>
                        <td style={{ padding: '12px' }}>{tournament.tournamentname || '-'}</td>
                        <td style={{ padding: '12px' }}>{getTournamentType(tournament.numberofplayers)}</td>
                        <td style={{ padding: '12px' }}>{tournament.totalplayers || '-'}</td>
                        <td style={{ padding: '12px' }}>{tournament.player2 || '-'}</td>
                        <td style={{ padding: '12px' }}>{tournament.player3 || '-'}</td>
                        <td style={{ padding: '12px' }}>{tournament.player4 || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                {getTranslation('dodaj-turniej-button', language)}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Add Tournament Form
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem',
              textAlign: 'center'
            }}>
              {getTranslation('dodaj-nowy-turniej', language)}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('data-required', language)}
                </label>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
                  <DatePicker
                    label={getTranslation('data', language)}
                    value={formData.tournamentdate ? new Date(formData.tournamentdate) : null}
                    onChange={(date) => {
                      const dateString = date ? date.toISOString().split('T')[0] : '';
                      handleInputChange({ target: { name: 'tournamentdate', value: dateString } });
                    }}
                    format={language === 'pl' ? 'dd/MM/yyyy' : language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
                    slotProps={{
                      textField: {
                        required: true,
                        placeholder: language === 'pl' ? 'dd/mm/rrrr' : language === 'fr' ? 'jj/mm/aaaa' : 'mm/dd/yyyy',
                        style: {
                          width: '100%',
                          fontFamily: 'Nunito, Arial, sans-serif'
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('nazwa-turnieju-required', language)}
                </label>
                <input
                  type="text"
                  name="tournamentname"
                  value={formData.tournamentname}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('miasto-required', language)}
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('typ-required', language)}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { value: 'single', label: getTranslation('single', language) },
                    { value: 'doubles', label: getTranslation('dublety', language) },
                    { value: 'triples', label: getTranslation('triplety', language) },
                    { value: 'triples_reserve', label: getTranslation('triplety-z-rezerwowym', language) }
                  ].map((option) => (
                    <label key={option.value} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '14px'
                    }}>
                      <input
                        type="radio"
                        name="tournamentType"
                        value={option.value}
                        checked={formData.tournamentType === option.value}
                        onChange={handleInputChange}
                        style={{ margin: 0 }}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
              
              {formData.tournamentType === 'doubles' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontWeight: '600',
                    color: '#1e3a8a'
                  }}>
                    {getTranslation('gracz2-required', language)}
                  </label>
                  <input
                    type="text"
                    name="player2"
                    value={formData.player2}
                    onChange={handleInputChange}
                    maxLength={30}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              
              {(formData.tournamentType === 'triples' || formData.tournamentType === 'triples_reserve') && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontWeight: '600',
                      color: '#1e3a8a'
                    }}>
                      {getTranslation('gracz2-required', language)}
                    </label>
                    <input
                      type="text"
                      name="player2"
                      value={formData.player2}
                      onChange={handleInputChange}
                      maxLength={30}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontWeight: '600',
                      color: '#1e3a8a'
                    }}>
                    {getTranslation('gracz3-required', language)}
                  </label>
                    <input
                      type="text"
                      name="player3"
                      value={formData.player3}
                      onChange={handleInputChange}
                      maxLength={30}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </>
              )}
              
              {formData.tournamentType === 'triples_reserve' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontWeight: '600',
                    color: '#1e3a8a'
                  }}>
                    {getTranslation('rezerwowy', language)}
                  </label>
                  <input
                    type="text"
                    name="player4"
                    value={formData.player4}
                    onChange={handleInputChange}
                    maxLength={30}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  {getTranslation('anuluj', language)}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  {submitting ? getTranslation('dodawanie', language) : getTranslation('dodaj', language)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.3rem'
            }}>
              {getTranslation('potwierdzenie-usuniecia', language)}
            </h3>
            <p style={{
              color: '#374151',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '30px',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {getTranslation('usuniecie-potwierdzenie-tekst', language)}
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {getTranslation('nie', language)}
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {getTranslation('tak', language)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTournament;
